import React, { useState, useEffect, ReactNode, useCallback } from "react";
import { Screen, ScreenData } from "@/models/screen";
import { Ad } from "@/models/ad";
import { Sale } from "@/models/sale";
import { toast } from "sonner";
import { 
  getScreenById, 
  getAdById, 
  getAdsByScreenId,
  getSalesBySellerGrouped
} from "./data/dataHelpers";
import { DataContextType } from "./DataContext.types";
import { useScreensCrud } from "./dataProvider/useScreensCrud";
import { useAdsCrud } from "./dataProvider/useAdsCrud";
import { useSellerStats } from "./dataProvider/useSellerStats";
import { useLoadData } from "./dataProvider/useLoadData";
import { DataContext } from "./DataContext";
import { useAuth } from "./AuthContext";
import { supabase } from "@/integrations/supabase/client";

// Clave para almacenar las pantallas favoritas en localStorage
const FAVORITE_SCREENS_KEY = 'favoriteScreens';

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [ads, setAds] = useState<Ad[]>([]);
  const [expiredAds, setExpiredAds] = useState<Ad[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [currentMonthSales, setCurrentMonthSales] = useState<Sale[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [favoriteScreenIds, setFavoriteScreenIds] = useState<string[]>([]);
  const { user } = useAuth();

  // Modificamos el hook useLoadData para incluir también los anuncios vencidos
  const { loadData } = useLoadData(setScreens, setAds, setSales, setIsLoading, setCurrentMonthSales, setExpiredAds);

  useEffect(() => {
    loadData();
    // Add this log to see when data is loaded
    console.log("DataProvider initialized, loading data...");

    // Cargar pantallas favoritas del localStorage si existe un usuario
    if (user?.id) {
      const key = `${FAVORITE_SCREENS_KEY}_${user.id}`;
      const savedFavorites = localStorage.getItem(key);
      if (savedFavorites) {
        try {
          setFavoriteScreenIds(JSON.parse(savedFavorites));
        } catch (error) {
          console.error("Error al cargar pantallas favoritas:", error);
        }
      }
    }
  }, [user?.id]);

  // Debug log para verificar datos de pantallas
  useEffect(() => {
    console.log("DataProvider - Datos cargados:", {
      screens: screens.length,
      activeAds: ads.length,
      expiredAds: expiredAds.length,
      sales: sales.length,
      currentMonthSales: currentMonthSales.length,
      user: user?.id,
      favoriteScreens: favoriteScreenIds.length
    });
    
    if (screens.length > 0) {
      console.log("Ejemplo de pantalla:", screens[0]);
    }
  }, [screens, ads, expiredAds, sales, currentMonthSales, user, favoriteScreenIds]);

  // Guardar favoritos en localStorage cuando cambien
  useEffect(() => {
    if (user?.id) {
      const key = `${FAVORITE_SCREENS_KEY}_${user.id}`;
      localStorage.setItem(key, JSON.stringify(favoriteScreenIds));
    }
  }, [favoriteScreenIds, user?.id]);

  const toggleFavoriteScreen = (screenId: string) => {
    setFavoriteScreenIds(prev => {
      if (prev.includes(screenId)) {
        return prev.filter(id => id !== screenId);
      } else {
        return [...prev, screenId];
      }
    });
  };

  const isFavoriteScreen = (screenId: string) => {
    return favoriteScreenIds.includes(screenId);
  };

  const getFavoriteScreens = () => {
    return screens.filter(screen => favoriteScreenIds.includes(screen.id));
  };

  const refreshData = async () => {
    console.log("Refreshing data...");
    await loadData();
    return Promise.resolve();
  };

  // Función para buscar anuncios con filtros
  const searchAds = async (name?: string, date?: string) => {
    setIsLoading(true);
    try {
      const options = { 
        filterByName: name, 
        filterByDate: date 
      };
      const filteredAds = await fetchAds(options);
      setAds(filteredAds);
    } catch (error) {
      console.error("Error al buscar anuncios:", error);
      toast.error("Error al buscar anuncios");
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize the CRUD hooks
  const { addScreen: rawAddScreen, updateScreen, deleteScreen } = useScreensCrud(setIsLoading, refreshData);
  const { addAd: rawAddAd, updateAd: rawUpdateAd, deleteAd: rawDeleteAd } = useAdsCrud(setIsLoading, refreshData);
  const getSellerStats = useSellerStats(ads, screens);

  // Wrap the addScreen function to adapt the interface
  const addScreen = async (data: ScreenData): Promise<void> => {
    // Convert from ScreenData to the format expected by rawAddScreen
    await rawAddScreen({
      name: data.name,
      location: data.location,
      totalSpaces: data.totalSpaces,
      occupiedSpaces: data.occupiedSpaces
    });
  };

  // Wrap the addAd function to return void instead of the newAd
  const addAd = async (data: any): Promise<void> => {
    await rawAddAd(data);
    // We discard the return value to match the Promise<void> type
  };

  // Wrap the updateAd function to return void instead of boolean
  const updateAd = async (data: Ad): Promise<void> => {
    await rawUpdateAd(data);
    // We discard the boolean return value to match the Promise<void> type
  };

  // Función para actualizar el estado local después de eliminar un anuncio
  const handleAdDeleted = useCallback((adId: string) => {
    console.log("Actualizando estado local después de eliminar anuncio:", adId);
    
    // Eliminar de la lista de anuncios activos
    setAds(prevAds => prevAds.filter(ad => ad.id !== adId));
    
    // Eliminar de la lista de anuncios vencidos
    setExpiredAds(prevExpiredAds => prevExpiredAds.filter(ad => ad.id !== adId));
    
    // Actualizar las pantallas afectadas
    setScreens(prevScreens => {
      return prevScreens.map(screen => {
        // Verificar si el anuncio estaba en esta pantalla
        const screenAds = [...ads, ...expiredAds].filter(ad => 
          ad.screens.some(s => s.screenId === screen.id)
        );
        const wasAdInScreen = screenAds.some(ad => ad.id === adId);
        
        if (!wasAdInScreen) return screen;
        
        return {
          ...screen,
          occupiedSpaces: Math.max(0, screen.occupiedSpaces - 1),
          availableSpaces: Math.min(screen.totalSpaces, screen.availableSpaces + 1),
          occupancyRate: screen.totalSpaces > 0 
            ? Math.round(((screen.occupiedSpaces - 1) / screen.totalSpaces) * 100) 
            : 0
        };
      });
    });
  }, [ads, expiredAds]);

  // Use the corrected getSalesBySellerGrouped function that now returns the expected type
  const getSalesSellerGrouped = (): { [sellerId: string]: Sale[] } => {
    return getSalesBySellerGrouped(sales);
  };

  // Función mejorada para ocultar un anuncio vencido
  const hideExpiredAd = async (adId: string) => {
    try {
      console.log("Iniciando proceso para ocultar anuncio:", adId);
      
      // Verificar que el anuncio existe en el estado local
      const adToHide = expiredAds.find(ad => ad.id === adId);
      if (!adToHide) {
        console.error("Anuncio no encontrado en el estado local:", adId);
        toast.error("Anuncio no encontrado");
        return;
      }

      console.log("Anuncio encontrado:", adToHide.clientName);
      
      // Verificar que el usuario está autenticado
      if (!user?.id) {
        console.error("Usuario no autenticado");
        toast.error("Debe estar autenticado para realizar esta acción");
        return;
      }

      console.log("Usuario autenticado:", user.id);
      
      // Actualizar el anuncio usando RPC
      const { data, error } = await supabase
        .rpc('hide_expired_ad', {
          ad_id: adId.toString(),
          user_id: user.id.toString()
        });

      if (error) {
        console.error("Error detallado en Supabase:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        toast.error(`Error al ocultar el anuncio: ${error.message}`);
        return;
      }

      if (data?.success) {
        // Actualizar el estado local solo si la operación fue exitosa
        setExpiredAds(prev => prev.filter(ad => ad.id !== adId));
        console.log("Anuncio ocultado exitosamente");
        toast.success("Anuncio ocultado de la pantalla");
      } else {
        console.error("No se pudo ocultar el anuncio");
        toast.error("No se pudo ocultar el anuncio");
      }
      
    } catch (error) {
      console.error("Error inesperado completo:", {
        error,
        message: error instanceof Error ? error.message : 'Error desconocido',
        stack: error instanceof Error ? error.stack : 'Sin stack trace'
      });
      toast.error("Error inesperado al ocultar el anuncio");
    }
  };

  // Los anuncios vencidos se mantienen sin filtrar para la sección de historial
  const value: DataContextType = {
    screens,
    // Asegurarnos de que no haya duplicados en las listas
    ads: ads.filter(ad => !ad.isExpired && !expiredAds.some(expAd => expAd.id === ad.id)),
    expiredAds,
    sales,
    currentMonthSales,
    isLoading,
    getScreenById: (id: string) => getScreenById(screens, id),
    getAdById: (id: string) => {
      console.log("Buscando anuncio por ID:", id);
      // Asegurarnos de que no haya duplicados al buscar un anuncio
      const activeAd = ads.find(ad => ad.id === id);
      if (activeAd) return activeAd;
      return expiredAds.find(ad => ad.id === id);
    },
    // Filtramos solo en la vista general de anuncios
    getAdsByScreenId: (screenId: string) => {
      console.log("Buscando anuncios para pantalla:", screenId);
      // Filtrar solo anuncios activos (no vencidos)
      const today = new Date();
      const filteredAds = ads.filter(ad => {
        const endDate = new Date(ad.endDate);
        const isExpired = endDate < today;
        console.log(`Anuncio ${ad.clientName} - Vencido: ${isExpired}, Fecha fin: ${ad.endDate}`);
        return ad.screens.some(screen => screen.screenId === screenId) && !isExpired;
      });
      console.log("Anuncios activos encontrados para pantalla:", filteredAds.length);
      return filteredAds;
    },
    getActiveAdsByScreenId: (screenId: string) => {
      const activeAds = getAdsByScreenId(ads.filter(ad => !ad.isExpired), screenId);
      console.log(`Anuncios activos para pantalla ${screenId}:`, activeAds.length);
      return activeAds;
    },
    // Filtramos anuncios vencidos que no estén ocultos solo para la vista de pantalla
    getExpiredAdsByScreenId: (screenId: string) => {
      const allExpiredAds = expiredAds;
      console.log("Total anuncios vencidos:", allExpiredAds.length);
      
      const filteredExpiredAds = allExpiredAds.filter(ad => {
        const isVisible = !ad.isHidden;
        const isForScreen = ad.screens.some(screen => screen.screenId === screenId);
        console.log(`Anuncio vencido ${ad.clientName} - Visible: ${isVisible}, Para pantalla: ${isForScreen}`);
        return isForScreen && isVisible;
      });
      
      console.log(`Anuncios vencidos visibles para pantalla ${screenId}:`, filteredExpiredAds.length);
      return filteredExpiredAds;
    },
    getSalesBySellerGrouped: getSalesSellerGrouped,
    addScreen,
    updateScreen,
    deleteScreen,
    addAd,
    updateAd,
    deleteAd: async (id: string): Promise<void> => {
      try {
        setIsLoading(true);
        console.log("Iniciando proceso de eliminación para anuncio:", id);
        
        // Primero actualizamos el estado local
        handleAdDeleted(id);
        
        // Luego intentamos eliminar en Supabase
        await rawDeleteAd(id);
        
        console.log("Anuncio eliminado exitosamente de Supabase");
        
        // No recargamos todos los datos, solo actualizamos las pantallas si es necesario
        const { data: screenData } = await supabase
          .from('screens')
          .select('*');
          
        if (screenData) {
          setScreens(screenData.map(screen => ({
            id: screen.id,
            name: screen.name,
            location: screen.location || '',
            max_ads: screen.max_ads,
            occupied_spaces: screen.occupied_spaces || 0,
            available_spaces: screen.available_spaces || screen.max_ads,
            occupancy_rate: screen.occupancy_rate || 0,
            created_at: screen.created_at,
            totalSpaces: screen.max_ads,
            occupiedSpaces: screen.occupied_spaces || 0,
            availableSpaces: screen.available_spaces || screen.max_ads,
            occupancyRate: screen.occupancy_rate || 0
          })));
        }
      } catch (error) {
        console.error("Error al eliminar anuncio:", error);
        // Si falla la eliminación en Supabase, recargamos los datos para restaurar el estado
        await loadData();
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    refreshData,
    getSellerStats,
    searchAds,
    toggleFavoriteScreen,
    isFavoriteScreen,
    getFavoriteScreens,
    hideExpiredAd,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

// Importamos la función de fetchAds con la nueva interfaz
import { fetchAds } from './data/fetchAds';
